import { Injectable } from "@angular/core";
import {
  AlertController,
  LoadingController,
  NavController,
  Platform,
} from "@ionic/angular";
import { EnumService } from "./enum.service";
import { UntypedFormControl, UntypedFormGroup, Validators } from "@angular/forms";
import { ValidatorService } from "./validator.service";
import * as moment from "moment";
import { StaticDataService } from "./static-data.service";
import { environment } from "../../environments/environment";
import { Capacitor } from "@capacitor/core";
import { File } from "@ionic-native/file/ngx";
import { TranslateService } from "@ngx-translate/core";
import { BehaviorSubject } from 'rxjs';

declare global {
  interface Array<T> {
    clone(): Array<T>;
  }
}

Array.prototype.clone = function () {
  return JSON.parse(JSON.stringify(this));
};

@Injectable({
  providedIn: "root",
})
export class UtilService {
  loading: HTMLIonLoadingElement = null;
  loadingStarting = false;
  loadingStopping = false;
  isLoadingRequestedInitiated = false;

  questionElementIds: Array<string>;
  questionElementIdsUpdate: any; // callback
  translateService: TranslateService;
  private hiddenSectionsSubject = new BehaviorSubject<any[]>([]);
  private hiddenQuestionsSubject = new BehaviorSubject<any[]>([]);

  private hideLogicExecutedSource = new BehaviorSubject<{type: 'question' | 'section', id: string} | null>(null);

  hiddenSections$ = this.hiddenSectionsSubject.asObservable();
  hiddenQuestions$ = this.hiddenQuestionsSubject.asObservable();

  hideLogicExecuted$ = this.hideLogicExecutedSource.asObservable();

  public removeHiddenSectionTriggered$ = new BehaviorSubject<string | null>(null);
  public addHiddenQuestionTriggered$ = new BehaviorSubject<{questionId: any, sectionId: any} | null>(null);

  get hiddenQuestions(): any[] {
    return this.hiddenQuestionsSubject.value;
  }

  get hiddenSections(): any[] {
    return this.hiddenSectionsSubject.value;
  }

  static scoreMatchAnswerColor() {
    return true;
  }

  static modifyUDID(udid) {
    if (!udid || udid.length === 0) {
      return "This device cannot be used in Dedicated Mode";
    }
    return udid;
  }

  static isExpired(
    expirationDate,
    companyTimeDifference,
    isExpireAfterExpiryDate = false
  ) {
    const timeDifference = parseInt(companyTimeDifference);
    const expiryDate = moment.utc(
      UtilService.fixTimeString(expirationDate),
      StaticDataService.dateTimeFormat
    );

    const currDate = moment.utc();
    currDate.add(timeDifference, "minutes");

    const daysCount = expiryDate.diff(currDate, "minutes");
    return isExpireAfterExpiryDate ? daysCount < 0 : daysCount <= 0;
  }

  static durationNow(date1, date2, withAgo = true) {
    let b = moment(
      date1.format(StaticDataService.dateTimeFormatForDb),
      StaticDataService.dateTimeFormatForDb
    ),
      a = moment(
        date2.format(StaticDataService.dateTimeFormatForDb),
        StaticDataService.dateTimeFormatForDb
      ),
      intervals: any = ["years", "months", "days", "hours", "minutes"],
      out = [];

    for (let i = 0; i < intervals.length; i++) {
      let diff = a.diff(b, intervals[i]);
      b.add(diff, intervals[i]);
      if (diff > 0) {
        out.push(diff + " " + intervals[i]);
      }
    }
    return out.join(", ") + (withAgo ? " ago" : "");
  }

  static todayCompanyDate(timeDifference, isOnlyDate = true) {
    const currentDate = moment().utc();

    currentDate.add(parseInt(timeDifference), "minutes");
    return isOnlyDate
      ? moment(
        currentDate.format(StaticDataService.dateZeroTimeFormat),
        StaticDataService.dateTimeFormatForDb
      )
      : currentDate;
  }

  static isLocalHost() {
    return (
      window.location?.host?.indexOf("localhost") !== -1 && !Capacitor.isNative
    );
  }

  static isWebApp() {
    return (
      /*window.location?.host?.indexOf("localhost") === -1 &&*/
      !environment.isFormPreview &&
      !Capacitor.isNative
    );
  }

  static appendZero(num) {
    if (num < 10) {
      return "0" + num;
    }
    return num;
  }

  static colorMap = {
    "black": "#000000",
    "green": "#7ED321",
    "gold": "#df951b",
    "yellow": "#d1c20b",
    "red": "#D0021B",
    "blue": "#4A90E2",
    "grey": "#9f9898",
    "turquoise": "#50E3C2",
    "purple": "#9150E3",
    "orange": "#EE5A35",
    "white": "#000"
  };

  static colorCache = {};

  static getSimpleColorForAnswerChoice(color) {
    return this.colorMap[color] || color;
  }

  static getColorForAnswerChoice(color) {
    switch (color) {
      case "black":
        return "#000000";
      case "green":
        return "#7ED321";
      case "gold":
        return "#df951b";
      case "yellow":
        return "#d1c20b";
      case "red":
        return "#D0021B";
      case "blue":
        return "#4A90E2";
      case "grey":
        return "#9f9898";
      case "turquoise":
        return "#50E3C2";
      case "purple":
        return "#9150E3";
      case "orange":
        return "#EE5A35";
      case "white":
        return "#000";
    }
    return color;
  }

  static FixBase64String(base64String) {
    try{
    return base64String
      ? base64String.replace(
        /^data:(image|video)\/(png|jpeg|jpg|gif|mov|mp4|mpeg|mpg|wmv|webm);base64,/,
        ""
      ).trim()
      : base64String;
      } catch{
        return null
      }
  }

  static IsBase64Sring(base64String) {
    try {
      return (
        btoa(atob(UtilService.FixBase64String(base64String))) ==
        UtilService.FixBase64String(base64String)
      );
    } catch (error) { }
    return false;
  }

  static InductionContentTypeScreenIdentify(
    contentType,
    isDedicatedMode = false
  ) {
    let routeName = "";
    switch (contentType) {
      case EnumService.InductionContentTypes.VIDEO_FILE:
        routeName = "checkin-induction-video-file";
        break;
      case EnumService.InductionContentTypes.IMAGE_FILE:
        routeName = "checkin-induction-image-file";
        break;
      case EnumService.InductionContentTypes.RICH_TEXT:
        routeName = "checkin-induction-rich-text";
        break;
      case EnumService.InductionContentTypes.FORM:
        // routeName = 'checkin-induction-form';
        routeName = isDedicatedMode ? "form-cover-dm" : "form-cover";
        break;
      case EnumService.InductionContentTypes.VISITOR_AGREEMENT:
        routeName = "checkin-induction-va";
        break;
    }
    return routeName;
  }

  static getFullName = (firstName, middleName, lastName) => {
    const names = [];
    if (firstName) {
      names.push(firstName);
    }
    if (middleName) {
      names.push(middleName);
    }
    if (lastName) {
      names.push(lastName);
    }
    return names.join(" ");
  };

  static IsFileImageType(fileNameOrExtension = "") {
    const type = fileNameOrExtension?.split(".").pop()?.toLowerCase();
    let isImage = false;
    switch (type) {
      case "png":
      case "jpeg":
      case "jpg":
      case "gif":
      case "image":
        isImage = true;
        break;
      default:
    }
    return isImage;
  }

  static FileIcon(fileNameOrExtension = "") {
    let iconName = "";
    const type = fileNameOrExtension?.split(".").pop()?.toLowerCase();

    switch (type) {
      case "docx":
      case "doc":
      case "docm":
      case "word":
      case "document":
        iconName = "attachment3";
        break;
      case "xlsx":
        iconName = "attachment1";
        break;
      case "pdf":
        iconName = "attachment2";
        break;
      case "form":
        iconName = "attachment3";
        break;
      case "folder":
        iconName = "attachment4";
        break;
      case "png":
      case "jpeg":
      case "jpg":
      case "gif":
      case "image":
        iconName = "attachment5";
        break;
      case "video":
      case "mp4":
      case "mov":
      case "mpeg":
      case "mpg":
      case "wmv":
      case "webm":
      case "m4v":
      case "avi":
      case "mkv":
        iconName = "attachment6";
        break;
      default:
        iconName = "attachment2";
    }
    return "./assets/icon/" + iconName + ".svg";
  }

  static isArray(obj) {
    return !!obj && obj.constructor === Array;
  }

  static isObject(obj) {
    return !!obj && obj.constructor === Object;
  }

  static rgbToHex(r: number, g: number, b: number): string {
    return `#${((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1)}`;
  }
  static calculateLighterShade(rgb: { r: number, g: number, b: number }, factor: number): string {
    const res = {
      r: Math.round(rgb.r * (1 - factor) + 255 * factor),
      g: Math.round(rgb.g * (1 - factor) + 255 * factor),
      b: Math.round(rgb.b * (1 - factor) + 255 * factor)
    };
    return UtilService.rgbToHex(res.r, res.g, res.b);
  }


  static hexToRgb(hex: string): { r: number, g: number, b: number } {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    return result ? {
      r: parseInt(result[1], 16),
      g: parseInt(result[2], 16),
      b: parseInt(result[3], 16)
    } : null;
  }

  static findObj(list, key, value, defaultIndex = 0) {
    if (list && list.length > 0) {
      let resultData = list[defaultIndex];
      list.some((data) => {
        if (data[key] === value || data[key] === parseInt(value)) {
          resultData = data;
          return true;
        }
      });
      return resultData;
    }
    return null;
  }

  static randomBoolean() {
    return Math.round(Math.random());
  }

  static randomNumber() {
    return Math.floor(Math.random() * 1000000 + 1);
  }

  static formattedNumberToNumber(formattedNumber) {
    return formattedNumber
      ? Number(String(formattedNumber).replace(/[^0-9.-]+/g, ""))
      : 0;
  }

  /**
   * Format seconds to "MM:SS"
   * @param s
   * @returns
   */

  static formatMSS(s) {
    // accepts seconds as Number or String. Returns m:ss
    const m = (s - (s %= 60)) / 60;
    return 9 < m
      ? ""
      : "0" +
      m + // and divide the resulting Number by 60
      // (can never result in a fractional value = no need for rounding)
      // to which we concatenate a String (converts the Number to String)
      // who's reference is chosen by the conditional operator:
      (9 < s // if    seconds is larger than 9
        ? ":" // then  we don't need to prepend a zero
        : ":0") + // else  we do need to prepend a zero
      s; // and we add Number s to the string (converting it to String as well)
  }

  static FCUniqueName(section, question) {
    let preString = "";
    let isSectionDuplicate = false;
    if (section) {
      isSectionDuplicate =
        section[EnumService.QuestionLogic.ActionTypeForForm.Duplicate];
    }

    let isQuestionDuplicate = false;
    if (question) {
      isQuestionDuplicate =
        question[EnumService.QuestionLogic.ActionTypeForForm.Duplicate];
    }
    if (
      section &&
      section[EnumService.QuestionLogic.FormControlNamePreStringForUniqueName]
    ) {
      preString =
        preString +
        "" +
        section[
        EnumService.QuestionLogic.FormControlNamePreStringForUniqueName
        ];
    }
    if (
      question &&
      question[EnumService.QuestionLogic.FormControlNamePreStringForUniqueName]
    ) {
      preString =
        preString +
        "" +
        question[
        EnumService.QuestionLogic.FormControlNamePreStringForUniqueName
        ];
    }
    return (
      preString +
      "FormControl_" +
      (isSectionDuplicate ? "Duplicate_" : "") +
      section.sectionId +
      "_" +
      (isQuestionDuplicate ? "Duplicate_" : "") +
      question.questionId
    );
  }

  /**
   *  Dynamic FormControlName for multiple checkbox
   */
  static SubFCName(fcName, optionId) {
    return fcName + "_SubFormControl_" + optionId;
  }

  static FCNameAdditioanlNoteUq(formControlName) {
    return "FormControlAdditionalNote_" + formControlName;
  }

  static FCNameActionUq(formControlName) {
    return "FormControlAction_" + formControlName;
  }

  /**
   *  Dynamic Html Element id
   */
  static HtmlElementIdUq(sectionIndex, questionIndex, sectionId, questionId) {
    return (
      "Question_" +
      sectionIndex +
      "_" +
      questionIndex +
      "_" +
      sectionId +
      "_" +
      questionId
    );
  }

  static fixTimeString(value) {
    return value.replace("T", " ");
  }

  static fixDeviceDirPath(dirpath) {
    return dirpath.includes("file://") ? dirpath : "file://" + dirpath;
  }

  static convertToObject(queryString) {
    let i = 0,
      retObj = {},
      pair = null,
      //get the query string, omitting the "?"
      //use the ampersand as a separator
      qArr = queryString.split("&");

    //each element in qArr is not a key/val pair
    //so we need to turn each one of these pairs
    //into a two-element array
    for (; i < qArr.length; i++) {
      //use the "=" as a separator
      pair = qArr[i].split("=");
      //pair is now a two-element array
      //so the "key" is the first element of that array
      //and the "val" is the second element
      //so now we just add this "pair" to our return object
      retObj[pair[0]] = pair[1];
    }

    //return the new object
    return retObj;
  }

  static convertToQuerystring(obj) {
    let str = [];
    for (const p in obj)
      if (obj.hasOwnProperty(p)) {
        str.push(encodeURIComponent(p) + "=" + encodeURIComponent(obj[p]));
      }
    return str.join("&");
  }

  static getQueryStringValue = (url, key) => {
    return decodeURIComponent(
      url.replace(
        new RegExp(
          "^(?:.*[&\\?]" +
          encodeURIComponent(key).replace(/[\.\+\*]/g, "\\$&") +
          "(?:\\=([^&]*))?)?.*$",
          "i"
        ),
        "$1"
      )
    );
  };

  static fireCallBack(callBack = null, data = null) {
    if (callBack) {
      callBack(data);
    }
  }

  static crop(url, aspectRatio) {
    return new Promise((resolve, reject) => {
      const inputImage = new Image();

      inputImage.onload = () => {
        const inputWidth = inputImage.naturalWidth;
        const inputHeight = inputImage.naturalHeight;
        const inputImageAspectRatio = inputWidth / inputHeight;

        let outputWidth = inputWidth;
        let outputHeight = inputHeight;

        if (inputImageAspectRatio > aspectRatio) {
          outputWidth = inputHeight * aspectRatio;
        } else if (inputImageAspectRatio < aspectRatio) {
          outputHeight = inputWidth / aspectRatio;
        }

        const outputImage = document.createElement("canvas");
        const ctx = outputImage.getContext("2d");

        outputImage.width = outputWidth;
        outputImage.height = outputHeight;

        const outputX = (outputWidth - inputWidth) * 0.5;
        const outputY = (outputHeight - inputHeight) * 0.5;

        ctx.drawImage(inputImage, outputX, outputY);

        try {
          const finalImage = outputImage.toDataURL("image/jpeg", 0.5);
          resolve(finalImage);
        } catch (error) {
          console.log('error resolving cropped image: ', error);
          reject(error);
        }
      };

      inputImage.onerror = (error) => {
        console.log('error drawing cropped image: ', JSON.stringify(error, ["message", "arguments", "type", "name"]));
        reject(error);
      };

      if (url.hasOwnProperty("dataUrl")) {
        inputImage.src = url.dataUrl;
      } else {
        inputImage.src = url;
      }
    });
  }


  static rotateImgYAxis(base64, rotate = 180) {
    return new Promise((resolve) => {
      const image = new Image();
      if (base64.hasOwnProperty("dataUrl")) {
        image.src = base64.dataUrl;
      } else {
        image.src = base64;
      }

      image.onload = () => {
        const inputWidth = image.naturalWidth;
        const inputHeight = image.naturalHeight;

        const canvas = document.createElement("canvas");

        canvas.width = inputWidth;
        canvas.height = inputHeight;

        const ctx = canvas.getContext("2d");
        ctx.save(); // save the current canvas state

        const horizontal = true,
          vertical = false;

        ctx.translate(image.width, image.height);
        // ctx.rotate((rotate * Math.PI) / rotate);
        ctx.setTransform(
          horizontal ? -1 : 1,
          0, // set the direction of x axis
          0,
          vertical ? -1 : 1, // set the direction of y axis
          horizontal ? image.width : 0, // set the x origin
          vertical ? image.height : 0 // set the y origin
        );
        ctx.drawImage(image, 0, 0);
        ctx.restore(); // restore the state as it was when this function was called

        // PNG. This read toDataURL("jpeg", 0.8); the quality argument only applies to lossy
        // formats, and "jpeg" is not a MIME type, so neither took effect and the output was
        // always PNG. Made explicit rather than changed - switching to real JPEG here would
        // alter the output of every caller, including any with a transparent source.
        const finalImage = canvas.toDataURL("image/png");
        if (base64.hasOwnProperty("dataUrl")) {
          base64.dataUrl = finalImage;
          resolve(base64);
        } else {
          resolve(finalImage);
        }
      };
    });
  }

  static convertBase64ToBlob(b64Data, contentType): Blob {
    contentType = contentType || "";
    const sliceSize = 512;
    b64Data = b64Data.replace(/^[^,]+,/, "");
    b64Data = b64Data.replace(/\s/g, "");
    const byteCharacters = window.atob(b64Data);
    const byteArrays = [];
    for (let offset = 0; offset < byteCharacters.length; offset += sliceSize) {
      const slice = byteCharacters.slice(offset, offset + sliceSize);
      const byteNumbers = new Array(slice.length);
      for (let i = 0; i < slice.length; i++) {
        byteNumbers[i] = slice.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      byteArrays.push(byteArray);
    }
    return new Blob(byteArrays, { type: contentType });
  }

  static isOfflineSycronizarionNeeded() {
    const lastSyncDate = localStorage.getItem(
      EnumService.LocalStorageKeys.SYNC_DATE_TIME
    );
    if (lastSyncDate) {
      const lastSyncDateObj = moment(lastSyncDate);
      const todayDateObj = moment();
      const dayDiff = todayDateObj.diff(lastSyncDateObj, "days");
      return dayDiff > StaticDataService.OfflineModeSyncMinDays;
    }
    return false;
  }

  static uniqueIdForWebApp() {
    let uniqueId = localStorage.getItem(
      EnumService.LocalStorageKeys.UNIQUE_WEBAPP_ID
    );
    if (!uniqueId) {
      const randomString = (length, chars) => {
        let result = "";
        for (var i = length; i > 0; --i)
          result += chars[Math.floor(Math.random() * chars.length)];
        return result;
      };
      uniqueId = (
        randomString(2, "ABCDEFGHIJKLMNOPQRSTUVWXYZ") +
        "-" +
        randomString(2, "0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZ") +
        "-" +
        randomString(2, "0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZ")
      ).toUpperCase();

      localStorage.setItem(
        EnumService.LocalStorageKeys.UNIQUE_WEBAPP_ID,
        uniqueId
      );
    }
    return uniqueId;
  }

  constructor(
    private loadingController: LoadingController,
    private file: File,
    private platform: Platform,
    private alertController: AlertController,
    private validatorService: ValidatorService
  ) { }

  getOfflineIconUrl(
    iconFileName: string,
    fileFormat: string,
    fileName?: string
  ): string {
    const extensionFromName = fileName?.includes(".")
      ? fileName.split(".").pop()
      : "";
    return UtilService.FileIcon(extensionFromName || fileFormat || "pdf");
  }

  getOfflineFileUrl(fileName, type) {
    if (!fileName && type === "icon") {
      return UtilService.FileIcon("pdf");
    }
    const deviceDirectory = this.file.dataDirectory;
    // Use the same fallback pattern as download code to handle null from localStorage
    const offlineFilesFolderName = localStorage.getItem(
      EnumService.LocalStorageKeys.OFFLINE_FILES_FOLDER_NAME
    ) || StaticDataService.offlineFilesFolderName;
    
    let localFilePath = deviceDirectory + "" + offlineFilesFolderName;

    const slash = "/";
    switch (type) {
      case "icon":
        localFilePath += slash + "images" + slash + fileName;
        break;
      case "user":
        localFilePath += slash + "user" + slash + fileName;
        break;
      case "document":
        localFilePath += slash + "document" + slash + fileName;
        const convertedUrl = Capacitor.convertFileSrc(localFilePath);
        console.log(`🔍 [GET OFFLINE URL] Generating offline file URL for document`);
        console.log(`🔍 [GET OFFLINE URL] Filename: ${fileName}`);
        console.log(`🔍 [GET OFFLINE URL] Platform: ${this.platform.is("ios") ? "iOS" : "Android"}`);
        console.log(`🔍 [GET OFFLINE URL] offlineFilesFolderName: ${offlineFilesFolderName}`);
        console.log(`🔍 [GET OFFLINE URL] Slash used: ${slash}`);
        console.log(`🔍 [GET OFFLINE URL] Raw file path (before convertFileSrc): ${localFilePath}`);
        console.log(`🔍 [GET OFFLINE URL] Converted URL (after convertFileSrc): ${convertedUrl}`);
        return convertedUrl;
      case "signoff":
      case "checkin":
        localFilePath += slash + "user_checkin_signoff" + slash + fileName;
        break;
      case "offline_user":
        localFilePath =
          deviceDirectory +
          "" +
          StaticDataService.offlineFilesFolderName +
          slash +
          fileName;
        break;
      default:
        break;
    }
    const convertedUrl = Capacitor.convertFileSrc(localFilePath);
    console.log(`🔍 [GET OFFLINE URL] Generated URL: ${convertedUrl}`);
    return convertedUrl;
    // return localFilePath;
  }

  generateThumbnailFromImage(
    img,
    MAX_WIDTH = 500,
    MAX_HEIGHT = 500,
    quality = 1,
    callback,
    optURL = ""
  ) {
    var canvas = document.createElement("canvas");
    var image = new Image();
  
    image.onerror = (
      event,
      source,
      lineno,
      colno,
      error
    ) => {
      //alert(`error generating thumbnail for image: ${event}, ${source}, ${lineno},${colno}, ${error}`);
      console.log(`error generating thumbnail for image: ${source}, ${lineno},${colno}, ${error}`, event);
    };
  
    image.onabort = (error) => {
      console.error("onabort", error);
    };
  
    image.onload = () => {
      var width = image.width;
      var height = image.height;
  
      if (width > height) {
        if (width > MAX_WIDTH) {
          height *= MAX_WIDTH / width;
          width = MAX_WIDTH;
        }
      } else {
        if (height > MAX_HEIGHT) {
          width *= MAX_HEIGHT / height;
          height = MAX_HEIGHT;
        }
      }
      canvas.width = width;
      canvas.height = height;
      var ctx = canvas.getContext("2d");
  
      ctx.drawImage(image, 0, 0, width, height);
  
      // IMPORTANT: 'jpeg' NOT 'jpg'
      var dataUrl = canvas.toDataURL("image/jpeg", quality);
  
      callback(dataUrl);
    };
  
    // Fetch the image with Cache-Control: no-cache header
    fetch(img, {
      headers: {
        'Cache-Control': 'no-cache'
      }
    })
    .then(response => response.blob())
    .then(blob => {
      const objectURL = URL.createObjectURL(blob);
      image.crossOrigin = "anonymous";
      image.src = objectURL;
    })
    .catch(error => {
      console.error('Failed to fetch image:', error);
    });
  }

  generateThumbnailFromBlob(
    blob: Blob,
    MAX_WIDTH = 500,
    MAX_HEIGHT = 500,
    quality = 1,
    callback,
  ) {
    const canvas = document.createElement("canvas");
    const image = new Image();
    let objectURL: string | null = URL.createObjectURL(blob);

    image.onerror = (event, source, lineno, colno, error) => {
      console.log(`error generating thumbnail from blob: ${source}, ${lineno},${colno}, ${error}`, event);
      if (objectURL) {
        URL.revokeObjectURL(objectURL);
        objectURL = null;
      }
    };

    image.onload = () => {
      let width = image.width;
      let height = image.height;

      if (width > height) {
        if (width > MAX_WIDTH) {
          height *= MAX_WIDTH / width;
          width = MAX_WIDTH;
        }
      } else {
        if (height > MAX_HEIGHT) {
          width *= MAX_HEIGHT / height;
          height = MAX_HEIGHT;
        }
      }
      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext("2d");
      ctx.drawImage(image, 0, 0, width, height);
      const dataUrl = canvas.toDataURL("image/jpeg", quality);

      if (objectURL) {
        URL.revokeObjectURL(objectURL);
        objectURL = null;
      }
      callback(dataUrl);
    };

    image.src = objectURL;
  }

  isBase64String(base64: string): boolean {
    //console.log("testing string:", base64.substring(0,100), "length:",base64.length)
    const validationMessage = this.findInvalidBase64Chars(base64);
    if (validationMessage === "No invalid characters found.") {
        return true;
    } else {
        console.warn(validationMessage);
        return false;
    }
}
  findInvalidBase64Chars(base64String: string): string {
  // Base64 character set (A-Z, a-z, 0-9, +, /, and padding =)
  const base64Pattern = /^[A-Za-z0-9+/=]+$/;
  let invalidChars = [];
  
  for (let i = 0; i < base64String.length; i++) {
      if (!base64Pattern.test(base64String[i])) {
          invalidChars.push({
              char: base64String[i],
              index: i
          });
      }
  }
  if (invalidChars.length > 0) {
      return invalidChars.map(item => `Invalid char "${item.char}" at index ${item.index}`).join(", ");
  }
  return "No invalid characters found.";
}

  sanitizeBase64(base64Data: string): string {
    const validMimeTypes = [
      'data:image/',
      'data:application/'
    ];
    // Check MIME type
    const mimeType = base64Data.split(',')[0];
    if (!validMimeTypes.some((type) => mimeType.startsWith(type))) {
      throw new Error('Invalid Base64 MIME type');
    }
    const base64Content = base64Data.split(',')[1];
    //console.log("header,content",mimeType, base64Content.substring(0,100))
    if (!mimeType || !base64Content) {
        throw new Error('Malformed Base64 data');
    }
    const sanitizedContent = base64Content.trim().replace(/[\r\n\s]+/g, '');
    const paddedContent = this.addBase64Padding(sanitizedContent);
    return `${mimeType},${paddedContent}`;
  }

  addBase64Padding(base64: string): string {
    const paddingNeeded = 4 - (base64.length % 4);
    if (paddingNeeded < 4) {
      return base64 + '='.repeat(paddingNeeded);
    }
    return base64;
  }

  urlToBase64(url, filename, mimeType) {
    return new Promise(async (resolve, reject) => {
      let dirpath = url.substr(0, url.lastIndexOf("/") + 1);
      dirpath = UtilService.fixDeviceDirPath(dirpath);

      try {
        const dirUrl = await this.file.resolveDirectoryUrl(dirpath);
        const retrievedFile = await this.file.getFile(dirUrl, filename, {});

        retrievedFile.file((data) => {
          // if (data.size > MAX_FILE_SIZE) return this.presentAlert("Error", "You cannot upload more than 5mb.");
          // if (data.type !== ALLOWED_MIME_TYPE) return this.presentAlert("Error", "Incorrect file type.");
          const reader = new FileReader();
          reader.readAsDataURL(data);
          reader.onload = function () {
            //console.log(reader.result);
            resolve(reader.result);
          };
          reader.onerror = function (error) {
            console.log("error converting url to base64: ", error);
            reject(error);
          };
        });
      } catch {
        reject();
      }
    });
  }

   getStringAfterPipe(input: string): string {
    const pipeIndex = input.indexOf('|');
    if (pipeIndex !== -1) {
        return input.substring(pipeIndex + 1);
    }
    return input;
   }
  
  removeCapacitorPrefix(url) {
      const prefix = 'capacitor://localhost/_capacitor_file_';
      if (url.startsWith(prefix)) {
      url = url.substring(prefix.length);
    }
    return url;
  }

  removeCapacitorPrefixAndroid(url) {
    try{ if (typeof(url) === 'object'){
    for (const key in url) {
      if (key.includes("zone_symbol__value")) {
          url = url[key]
      }}
  }} catch{}
      const prefix1 = 'http://localhost/_capacitor_file_';
      const prefix2 = 'https://localhost/_capacitor_file_';
      const prefix3 = 'file://https://localhost/_capacitor_file_';
      //try{
    if (url.startsWith(prefix1)) {
      url = url.substring(prefix1.length);   
    }else  if (url.startsWith(prefix2)) {
      url = url.substring(prefix2.length);   
    }
    else  if (url.startsWith(prefix3)) {
      url = url.substring(prefix3.length);   
    }
  //}
   /*catch{
    try{
    url = url.replace("/_capacitor_file_","")
    } catch{}
   }*/
   // console.log("url after prefix removed (3): ", url, typeof(url))
    return url;
  }
  removeCapacitorPrefixIOS(url) {
    const prefix1 = 'blob:capacitor://localhost';
    if (url.startsWith(prefix1)) {
      const location = window.location.origin;
      alert(`current location: ${location}`)
      //url = url.replace(prefix1,`blob:http://localhost:4200`);   //Should it just be localhost?
      if(this.platform.is("iphone")){
        url = url.replace(prefix1,`blob:http://localhost:4200`); 
        alert(`on iphone, setting: ${url}`)
      } else if (this.platform.is("ipad")){
        url = url.replace(prefix1, `blob:${location}`);
        alert(`on ipad, setting: ${url}`)
      }
    }
    return url;
  }
  dataUriToFile(url, filename, mimeType) {
    filename = this.getStringAfterPipe(filename).trim();
    if (this.platform.is('android')) { 
      url =this.removeCapacitorPrefixAndroid(url)
    }
    else {
      url= this.removeCapacitorPrefix(url);
    }

    return new Promise(async (resolve, reject) => {
      if (UtilService.IsBase64Sring(url)) {
        const byteString = window.atob(UtilService.FixBase64String(url));
        const arrayBuffer = new ArrayBuffer(byteString.length);
        const int8Array = new Uint8Array(arrayBuffer);
        for (let i = 0; i < byteString.length; i++) {
          int8Array[i] = byteString.charCodeAt(i);
        }
        const blob = new Blob([int8Array], { type: mimeType });
        //console.log("conversion data: bytes: ",byteString, " buffer: ", arrayBuffer, "int8Array: ", int8Array, "blob: ", blob)
        resolve(blob);
      } else {
        let dirpath = url.substr(0, url.lastIndexOf("/") + 1);
        dirpath = UtilService.fixDeviceDirPath(dirpath);

        try {
          console.log('yoyoy',dirpath,filename)
          const dirUrl = await this.file.resolveDirectoryUrl(dirpath);
          console.log('hghg',dirUrl)
          const checkValue = "/Camera/"
          console.log("values: ", dirUrl.fullPath , dirUrl.nativeURL)
          if(!dirUrl.fullPath.endsWith(checkValue)){
            const index = dirUrl.fullPath.lastIndexOf(checkValue);
            dirUrl.fullPath = dirUrl.fullPath.substring(0, index + dirUrl.fullPath.length)
            console.log(">><< changed full path to: ", dirUrl.fullPath)
          }
          if(!dirUrl.nativeURL.endsWith(checkValue)){
            const index = dirUrl.nativeURL.lastIndexOf(checkValue);
            dirUrl.nativeURL = dirUrl.nativeURL.substring(0, index + dirUrl.nativeURL.length)
            console.log(">><< change nativeURL to: ", dirUrl.nativeURL)
          }
          const retrievedFile = await this.file.getFile(dirUrl, filename, {});
          console.log('yoooo',retrievedFile)

          retrievedFile.file((data) => {  
            // if (data.size > MAX_FILE_SIZE) return this.presentAlert("Error", "You cannot upload more than 5mb.");
            // if (data.type !== ALLOWED_MIME_TYPE) return this.presentAlert("Error", "Incorrect file type.");
            const selectedVideo = retrievedFile.nativeURL;
            resolve(selectedVideo);
          });
        } catch (error) {
          console.log('>>>>> rejected error', JSON.stringify(error))
          reject(error);
        }
      }
    });
  }

  Uniqueid() {
    // always start with a letter (for DOM friendlyness)
    var idstr = String.fromCharCode(Math.floor(Math.random() * 25 + 65));
    do {
      // between numbers and characters (48 is 0 and 90 is Z (42-48 = 90)
      var ascicode = Math.floor(Math.random() * 42 + 48);
      if (ascicode < 58 || ascicode > 64) {
        // exclude all chars between : (58) and @ (64)
        idstr += String.fromCharCode(ascicode);
      }
    } while (idstr.length < 32);

    return idstr;
  }

  async presentLoadingWithOptions(message = "") {
    this.isLoadingRequestedInitiated = true;

    if (!this.loading && !this.loadingStarting) {
      this.loadingStopping = false;
      this.loadingStarting = true;
      this.loading = await this.loadingController.create({
        cssClass: "my-loading-class",
        spinner: null,
        message: '<div><ion-icon src="./assets/icon/Loader.svg"></ion-icon><div class="loading-info"></div>',
        translucent: false,
        backdropDismiss: false,
        mode: "md",
      });
      this.loadingStarting = false;
      await this.loading.present();
      if(message){
        const loadingElement = document.querySelector('.loading-info');
        const subtitle = document.createElement('p');
        subtitle.textContent = message;
        loadingElement.querySelector('.loading-wrapper')?.appendChild(subtitle);
      }
      if (this.loadingStopping) {
        this.hideLoading();
      }
    }
  }

  async privatePresentLoadingWithOptions() {
    const loading = await this.loadingController.create({
      cssClass: "my-loading-class",
      spinner: null,
      message: '<ion-icon src="./assets/icon/Loader.svg"/>',
      translucent: false,
      backdropDismiss: false,
      mode: "md",
    });
    await loading.present();
    return loading;
  }

  hideLoading() {
    this.isLoadingRequestedInitiated = false;

    setTimeout(() => {
      if (!this.isLoadingRequestedInitiated) {
        this.loadingStopping = true;
        if (this.loading) {
          this.loading.dismiss();
          this.loading = null;
        }
      }
    }, 500);
  }

  async showAlert(message = "", title = "", callBack = null) {
    this.translateService.get("SHARED_TEXT.CANCEL").subscribe(async (res) => {
      const alert = await this.alertController.create({
        cssClass: "my-custom-class",
        header: title,
        message,
        buttons: [
          {
            text: res,
            role: "cancel",
            cssClass: "secondary",
            handler: callBack,
          },
        ],
      });

      await alert.present();
    });
  }

  async showConfirmAlert(
    message = "",
    title = "",
    callBack = null,
    cancelBtnKey = "SHARED_TEXT.NO",
    okBtnKey = "SHARED_TEXT.YES"
  ) {
    this.translateService
      .get([cancelBtnKey, okBtnKey])
      .subscribe(async (res) => {
        const alert = await this.alertController.create({
          cssClass: "my-custom-class",
          header: title,
          message,
          buttons: [
            {
              text: res[cancelBtnKey],
              role: "cancel",
              cssClass: "secondary",
              handler: () => {
                callBack(false);
              },
            },
            {
              text: res[okBtnKey],
              handler: () => {
                callBack(true);
              },
            },
          ],
        });

        await alert.present();
      });
  }

  getCurrentTimeStamp() {
    return Math.floor(Date.now()) + Math.floor(Math.random() * 100000);
  }

  getCurrentDateTIme() {
    return new Date().toISOString().slice(0, 19).replace("T", " ");
  }

  public shouldShowSection(section) {
    return section[EnumService.QuestionLogic.ActionTypeForForm.ShowForLogic]
      ? true
      : section[EnumService.QuestionLogic.ActionTypeForForm.HideForLogic]
        ? false
        : !section.sectionIsHidden;
  }

  public shouldShowQuestion(question) {
    return question[EnumService.QuestionLogic.ActionTypeForForm.ShowForLogic]
      ? true
      : question[EnumService.QuestionLogic.ActionTypeForForm.HideForLogic]
        ? false
        : !question.questionIsHidden;
  }

  addDynamicFormControlsIfNotExist(
    section,
    sectionIndex,
    questionIndex,
    question,
    formGroup: UntypedFormGroup,
    onValueChange = null,
    savedFormData = null,
    onEmptyValueFound = null
  ) {
    const validators = [];
    if (question.questionIsRequired) {
      validators.push(Validators.required);
    }

    if (
      question.selectedAnswerTypeId ===
      EnumService.CustomAnswerType.NumberFieldDecimal
    ) {
      validators.push(Validators.pattern("\\d+([.]\\d+)?"));
    }
    if (
      question.selectedAnswerTypeId ===
      EnumService.CustomAnswerType.NumberFieldInteger
    ) {
      validators.push(Validators.pattern("-{0,1}[0-9]*"));
      validators.push(Validators.min(-999999999999999999));
      validators.push(Validators.max(999999999999999999));
    }

    const formControlName = UtilService.FCUniqueName(section, question);

    if (
      question.selectedAnswerTypeId ===
      EnumService.CustomAnswerType.MultipleChoiceSet
    ) {
      const checkboxFormGroup = new UntypedFormGroup(
        {},
        {
          validators: question.questionIsRequired
            ? this.validatorService.validateCheckboxes
            : null,
        }
      );

      let isDefaultValue = false;

      if (question.answerChoiceAttributes) {
        question.answerChoiceAttributes.map((choice) => {
          const fcName = UtilService.SubFCName(
            formControlName,
            choice.answerChoiceAttributeId
          );

          const defaultValue =
            savedFormData && savedFormData[formControlName]
              ? savedFormData[formControlName][fcName]
              : false;
          if (defaultValue) {
            isDefaultValue = true;
          }

          checkboxFormGroup?.addControl(
            fcName,
            new UntypedFormControl(defaultValue ? true : false)
          );
        });
      }

      if (!isDefaultValue) {
        onEmptyValueFound && onEmptyValueFound();
      }

      checkboxFormGroup.valueChanges.subscribe(onValueChange);
      if (!formGroup?.controls[formControlName]) {
        formGroup?.addControl(formControlName, checkboxFormGroup);
      }
    } else if (
      question.selectedAnswerTypeId ===
      EnumService.CustomAnswerType.BodyPartControl
    ) {
      const injuriesReported =
        savedFormData &&
        savedFormData[
        EnumService.AccidentCustomLocationControlsName.InjuriesReported
        ];
      const checkboxFormGroup = new UntypedFormGroup(
        {},
        {
          // validators: null,
          validators:
            injuriesReported === "yes"
              ? this.validatorService.validateCheckboxes
              : null,
        }
      );

      let isDefaultValue = false;

      question.bodyParts?.map((bodyPartGroup) => {
        bodyPartGroup.parts.map((part) => {
          const fcName = UtilService.SubFCName(formControlName, part.id);

          const defaultValue =
            savedFormData && savedFormData[formControlName]
              ? savedFormData[formControlName][fcName]
              : false;
          if (defaultValue) {
            isDefaultValue = true;
          }

          checkboxFormGroup?.addControl(
            fcName,
            new UntypedFormControl(defaultValue ? true : false)
          );
        });
      });
      if (!isDefaultValue) {
        onEmptyValueFound && onEmptyValueFound();
      }
      formGroup?.addControl(formControlName, checkboxFormGroup);
    } else if(question.selectedAnswerTypeId ===
      EnumService.CustomAnswerType.BodyMapCustomQuestion){
        const bodyMapFormGroup = new UntypedFormGroup({});
        let isDefaultValue = false;
        StaticDataService.bodyParts.map((partGroup) => {
          partGroup.parts.map((part: any) => {
            const fcName = UtilService.SubFCName(formControlName, part.id);
            const defaultValue = savedFormData && savedFormData[formControlName] ? savedFormData[formControlName][fcName] : false;
            if (defaultValue) { isDefaultValue = true; }
            bodyMapFormGroup.addControl(fcName, new UntypedFormControl(defaultValue ? true : false));
          });
        });
        if (!isDefaultValue) { onEmptyValueFound && onEmptyValueFound(); }
        if (!formGroup?.controls[formControlName]) { formGroup?.addControl(formControlName, bodyMapFormGroup); }
    } else if (
      question.selectedAnswerTypeId ===
      EnumService.CustomAnswerType.ConfirmationBox
    ) {
      const defaultValue = savedFormData
        ? savedFormData[formControlName]
        : false;
      if (!defaultValue) {
        onEmptyValueFound && onEmptyValueFound();
      }

      const control = new UntypedFormControl(
        defaultValue ? true : false,
        Validators.compose(validators)
      );
      control.valueChanges.subscribe(onValueChange);
      if (!formGroup?.controls[formControlName]) {
        formGroup?.addControl(formControlName, control);
      }
    } else {
      const defaultValue =
        savedFormData && savedFormData[formControlName]
          ? savedFormData[formControlName]
          : "";
      if (!defaultValue) {
        onEmptyValueFound && onEmptyValueFound();
      }
      const control = new UntypedFormControl(
        defaultValue,
        Validators.compose(validators)
      );
      control.valueChanges.subscribe(onValueChange);
      if (!formGroup?.controls[formControlName]) {
        formGroup?.addControl(formControlName, control);
      }
    }

    if (question.shouldShowOptionalComment) {
      const formControlAdditionalNote =
        UtilService.FCNameAdditioanlNoteUq(formControlName);
      if (!formGroup?.controls[formControlAdditionalNote]) {
        formGroup?.addControl(
          formControlAdditionalNote,
          new UntypedFormControl(
            savedFormData && savedFormData[formControlAdditionalNote]
              ? savedFormData[formControlAdditionalNote]
              : ""
          )
        );
      }
    }

    question.questionLogics.some((logic) => {
      if (logic.questionActionTypeID === 21) {
        const formControlAction =
          UtilService.FCNameActionUq(formControlName);
        if (!formGroup?.controls[formControlAction]) {
          formGroup?.addControl(
            formControlAction,
            new UntypedFormControl(
              savedFormData && savedFormData[formControlAction]
                ? savedFormData[formControlAction]
                : ""
            )
          );
        }
      }
    })

  }

  addFormControlsForVisibleFields = (
    sections,
    formGroup: UntypedFormGroup,
    savedFormData: any = null,
    onFirstEmptyValueFoundCallback = null
  ) => {
    let topMostEmptyQuestionElementId = null;

    const questionElementIds = [];
    if (sections) {
      sections.map((section, sectionIndex) => {
        // No need to add form control for Risk Assessment section because we are not using form controls, we using ngModel
        if (section.isRiskAssessmentSection) {
          const tasks = section.riskAssessmentAnswerDetails?.taskAnswers;

          tasks.map((task, questionIndex) => {
            const elementId = UtilService.HtmlElementIdUq(
              sectionIndex,
              questionIndex,
              section.sectionId,
              task.taskAnswerId
            );

            const hazardAnswers = task.hazardAnswers;
            let isAnyOneFieldEmpty = false;
            hazardAnswers.map((hazard, hazardIndex) => {
              if (!hazard.hazardAnswerTitle) {
                isAnyOneFieldEmpty = true;
              }
              if (!hazard.riskRatingSeverityID) {
                isAnyOneFieldEmpty = true;
              }
              if (!hazard.riskRatingProbabilityID) {
                isAnyOneFieldEmpty = true;
              }
              const controlMeasureAnswers = hazard.controlMeasureAnswers;
              controlMeasureAnswers.map(
                (controlMeasure, controlMeasureIndex) => {
                  if (!controlMeasure.controlMeasureAnswerTitle) {
                    isAnyOneFieldEmpty = true;
                  }
                }
              );

              if (!hazard.residualRiskRatingSeverityID) {
                isAnyOneFieldEmpty = true;
              }
              if (!hazard.residualRiskRatingProbabilityID) {
                isAnyOneFieldEmpty = true;
              }
              if (!hazard.residualRiskRatingCalculatedValue) {
                isAnyOneFieldEmpty = true;
              }
              if (hazard.isMembersOfTheWorkForce) {
                if (
                  !hazard.isMembersOfTheWorkForceUserIDs &&
                  !hazard.isMembersOfTheWorkForceUserGroupIDs
                ) {
                  isAnyOneFieldEmpty = true;
                }
              }
              if (
                hazard.isMembersOfThePublic &&
                !hazard.membersOfThePublicDescription
              ) {
                isAnyOneFieldEmpty = true;
              }
              if (
                hazard.isMembersOfTheWorkForce &&
                hazard.hasPersonnelExposedNotification
              ) {
                if (
                  !hazard.personnelExposedNotificationUserIDs &&
                  !hazard.personnelExposedNotificationUserGroupIDs
                ) {
                  isAnyOneFieldEmpty = true;
                }
              }
            });

            if (isAnyOneFieldEmpty && !topMostEmptyQuestionElementId) {
              topMostEmptyQuestionElementId = elementId;
            }

            if (
              this.shouldShowSection(section) &&
              this.shouldShowQuestion(task)
            ) {
              if (questionElementIds.indexOf(elementId) !== -1) {
                questionElementIds.splice(
                  questionElementIds.indexOf(elementId),
                  1
                );
              }
              questionElementIds.push(elementId);
            } else {
              const indexOfElement = questionElementIds.indexOf(elementId);
              if (indexOfElement !== -1) {
                questionElementIds.splice(indexOfElement, 1);
              }
            }
          });
        } // No need to add form control for Hav section because we are not using form controls, we using ngModel
        else if (section.isIOSHRiskAssessmentSection){
          const hazards = section.ioshRiskAssessmentAnswerDetails?.hazardAnswers;
          try{
          hazards.map((hazard, questionIndex) => {
            const elementId = UtilService.HtmlElementIdUq(
              sectionIndex,
              questionIndex,
              section.sectionId,
              hazard.IOSHHazardAnswerId
            );
            let isAnyOneFieldEmpty = false;
            if (!hazard.hazardAnswerTitle) {
              isAnyOneFieldEmpty = true;
            }
            if (!hazard.riskRatingSeverityID) {
              isAnyOneFieldEmpty = true;
            }
            if (!hazard.riskRatingProbabilityID) {
              isAnyOneFieldEmpty = true;
            }
            const controlMeasureAnswers = hazard.IOSHHazardControlAnswers;
            controlMeasureAnswers.map(
              (controlMeasure, controlMeasureIndex) => {
                if (!controlMeasure.controlMeasureAnswerTitle) {
                  isAnyOneFieldEmpty = true;
                }
              }
            );
            if (!hazard.residualRiskRatingSeverityID) {
              isAnyOneFieldEmpty = true;
            }
            if (!hazard.residualRiskRatingProbabilityID) {
              isAnyOneFieldEmpty = true;
            }
            if (!hazard.residualRiskRatingCalculatedValue) {
              isAnyOneFieldEmpty = true;
            }
            
            if (isAnyOneFieldEmpty && !topMostEmptyQuestionElementId) {
              topMostEmptyQuestionElementId = elementId;
            }
            if (
              this.shouldShowSection(section) &&
              this.shouldShowQuestion(hazard)
            ) {
              if (questionElementIds.indexOf(elementId) !== -1) {
                
                questionElementIds.splice(
                  questionElementIds.indexOf(elementId),
                  1
                );
              }
              questionElementIds.push(elementId);
            } else {
              const indexOfElement = questionElementIds.indexOf(elementId);
              if (indexOfElement !== -1) {
                questionElementIds.splice(indexOfElement, 1);
              }
            }
          })
        }
          catch{ console.log("Error setting up iosh section (ignore if loading save state)")}
        }
        else if (section.isHAVSection) {
          const havAssessmentTools = section.havAssessmentTools || [];

          havAssessmentTools.map(
            (havAssessmentTool, havAssessmentToolIndex) => {
              havAssessmentTool.questions.map((question, questionIndex) => {
                const elementId = UtilService.HtmlElementIdUq(
                  sectionIndex + "" + havAssessmentToolIndex,
                  questionIndex,
                  section.sectionId,
                  question.questionId
                );

                if (!question.value && !topMostEmptyQuestionElementId) {
                  topMostEmptyQuestionElementId = elementId;
                }

                if (
                  this.shouldShowSection(section) &&
                  this.shouldShowQuestion(question)
                ) {
                  if (questionElementIds.indexOf(elementId) !== -1) {
                    questionElementIds.splice(
                      questionElementIds.indexOf(elementId),
                      1
                    );
                  }
                  questionElementIds.push(elementId);
                } else {
                  const indexOfElement = questionElementIds.indexOf(elementId);
                  if (indexOfElement !== -1) {
                    questionElementIds.splice(indexOfElement, 1);
                  }
                }
              });
            }
          );
        } else {
          const questions = section.questions;
          questions.map((question, questionIndex) => {
            const elementId = UtilService.HtmlElementIdUq(
              sectionIndex,
              questionIndex,
              section.sectionId,
              question.questionId
            );

            if (
              section.isAccidentReportSection &&
              question.selectedAnswerTypeId ===
              EnumService.CustomAnswerType.LocationSelection
            ) {
              const controlName = UtilService.FCUniqueName(section, question);
              if (
                this.shouldShowSection(section) &&
                this.shouldShowQuestion(question)
              ) {
                const controlValue =
                  savedFormData && savedFormData[controlName]
                    ? savedFormData[controlName]
                    : "";

                const placeControlValue =
                  savedFormData &&
                    savedFormData[
                    EnumService.AccidentCustomLocationControlsName
                      .PlaceNotintheList
                    ]
                    ? savedFormData[
                    EnumService.AccidentCustomLocationControlsName
                      .PlaceNotintheList
                    ]
                    : false;
                const locationControlValue =
                  savedFormData &&
                    savedFormData[
                    EnumService.AccidentCustomLocationControlsName.LocationName
                    ]
                    ? savedFormData[
                    EnumService.AccidentCustomLocationControlsName
                      .LocationName
                    ]
                    : "";
                const injuriesReportedControlValue =
                  savedFormData &&
                    savedFormData[
                    EnumService.AccidentCustomLocationControlsName
                      .InjuriesReported
                    ]
                    ? savedFormData[
                    EnumService.AccidentCustomLocationControlsName
                      .InjuriesReported
                    ]
                    : "";

                if (
                  !controlValue &&
                  !placeControlValue &&
                  !locationControlValue &&
                  !injuriesReportedControlValue &&
                  !topMostEmptyQuestionElementId
                ) {
                  topMostEmptyQuestionElementId = elementId;
                }

                formGroup.addControl(
                  controlName,
                  new UntypedFormControl(controlValue)
                );
                formGroup.addControl(
                  EnumService.AccidentCustomLocationControlsName
                    .PlaceNotintheList,
                  new UntypedFormControl(placeControlValue ? true : false)
                );
                formGroup.addControl(
                  EnumService.AccidentCustomLocationControlsName.LocationName,
                  new UntypedFormControl(locationControlValue)
                );
                formGroup.addControl(
                  EnumService.AccidentCustomLocationControlsName
                    .InjuriesReported,
                  new UntypedFormControl(injuriesReportedControlValue)
                );

                if (questionElementIds.indexOf(elementId) !== -1) {
                  questionElementIds.splice(
                    questionElementIds.indexOf(elementId),
                    1
                  );
                }
                questionElementIds.push(elementId);
              } else {
                this.removeFieldIfAdded(formGroup, controlName);
                this.removeFieldIfAdded(
                  formGroup,
                  EnumService.AccidentCustomLocationControlsName
                    .PlaceNotintheList
                );
                this.removeFieldIfAdded(
                  formGroup,
                  EnumService.AccidentCustomLocationControlsName.LocationName
                );
                const indexOfElement = questionElementIds.indexOf(elementId);
                if (indexOfElement !== -1) {
                  questionElementIds.splice(indexOfElement, 1);
                }
              }
            } else {
              if (
                this.shouldShowSection(section) &&
                this.shouldShowQuestion(question)
              ) {
                this.addDynamicFormControlsIfNotExist(
                  section,
                  sectionIndex,
                  questionIndex,
                  question,
                  formGroup,
                  (value) => {
                    this.checkAndApplyLogic(
                      question,
                      value,
                      formGroup,
                      sections,
                      sectionIndex
                    );
                  },
                  savedFormData,
                  () => {
                    if (!topMostEmptyQuestionElementId) {
                      topMostEmptyQuestionElementId = elementId;
                    }
                  }
                );
                if (questionElementIds.indexOf(elementId) !== -1) {
                  questionElementIds.splice(
                    questionElementIds.indexOf(elementId),
                    1
                  );
                }
                questionElementIds.push(elementId);
              } else {
                const controlName = UtilService.FCUniqueName(section, question);
                this.removeFieldIfAdded(formGroup, controlName);
                const indexOfElement = questionElementIds.indexOf(elementId);
                if (indexOfElement !== -1) {
                  questionElementIds.splice(indexOfElement, 1);
                }
              }
            }
          });
        }
      });
    }
    UtilService.fireCallBack(this.questionElementIdsUpdate, questionElementIds);

    if (topMostEmptyQuestionElementId && savedFormData) {
      onFirstEmptyValueFoundCallback &&
        onFirstEmptyValueFoundCallback(topMostEmptyQuestionElementId);
    }
  };

  /**
   * Check logic for question and apply it to relevent section/question
   * @param question
   * @param value
   * @param formGroup
   * @param sections
   */
  checkAndApplyLogic(
    question,
    value,
    formGroup: UntypedFormGroup,
    sections,
    sectionIndex
  ) {
   // console.log("logic: ", value, formGroup)
    if (question.allowQuestionLogic) {
      const questionLogics = question.questionLogics;

      this.resetAppliedLogicByQuestion(
        question,
        sections,
        sectionIndex,
        formGroup
      );

      // check which logic applicable
      questionLogics.some((logic) => {
        if (
          EnumService.QuestionLogic.LogicApplicableForQuestionTypes.indexOf(
            question.selectedAnswerTypeId
          ) !== -1
        ) {
          let isActionMeet = false;
          if (
            question.selectedAnswerTypeId ===
            EnumService.CustomAnswerType.SingleChoiceSet
          ) {
            const questionChoiceSetSequence =
              logic.questionChoiceSetSequence - 1;
            const questionChoiceSetTypeID = logic.questionChoiceSetTypeID;
            const valueToBeCompare =
              question.answerChoiceAttributes[questionChoiceSetSequence];
            // Check if condition meet
            const isSelectedAndMeet =
              questionChoiceSetTypeID ===
              EnumService.QuestionLogic.SingleMultipleChoiceOperationType
                .Selected &&
              value === valueToBeCompare.answerChoiceAttributeId;
            const isNotSelectedAndMeet =
              questionChoiceSetTypeID ===
              EnumService.QuestionLogic.SingleMultipleChoiceOperationType
                .NotSelected &&
              value !== valueToBeCompare.answerChoiceAttributeId;

            isActionMeet = isSelectedAndMeet || isNotSelectedAndMeet;
          } else if (
            question.selectedAnswerTypeId ===
            EnumService.CustomAnswerType.MultipleChoiceSet
          ) {
            const questionChoiceSetSequence =
              logic.questionChoiceSetSequence - 1;
            const questionChoiceSetTypeID = logic.questionChoiceSetTypeID;
            const checkBoxValueSelected =
              value[Object.keys(value)[questionChoiceSetSequence]];
            // Check if condition meet
            const isSelectedAndMeet =
              questionChoiceSetTypeID ===
              EnumService.QuestionLogic.SingleMultipleChoiceOperationType
                .Selected && checkBoxValueSelected;
            const isNotSelectedAndMeet =
              questionChoiceSetTypeID ===
              EnumService.QuestionLogic.SingleMultipleChoiceOperationType
                .NotSelected && !checkBoxValueSelected;

            isActionMeet = isSelectedAndMeet || isNotSelectedAndMeet;
          } else if (
            question.selectedAnswerTypeId ===
            EnumService.CustomAnswerType.ConfirmationBox
          ) {
            const questionChoiceSetSequence =
              logic.questionChoiceSetSequence - 1;
            const questionChoiceSetTypeID = logic.questionChoiceSetTypeID;
            const valueToBeCompare =
              question.answerChoiceAttributes[questionChoiceSetSequence];
            // Check if condition meet
            const isSelectedAndMeet =
              questionChoiceSetTypeID ===
              EnumService.QuestionLogic.SingleMultipleChoiceOperationType
                .Selected &&
              (value || value === "true");
            const isNotSelectedAndMeet =
              questionChoiceSetTypeID ===
              EnumService.QuestionLogic.SingleMultipleChoiceOperationType
                .NotSelected && !value;

            isActionMeet = isSelectedAndMeet || isNotSelectedAndMeet;
          } else {
            const questionOperatorTypeID = logic.questionOperatorTypeID;

            let valueToBeCompared;
            let selectedValue;

            let dateObjToBeCompared;
            let selectedDateObj;

            let isDateTimeObject = false;

            switch (question.selectedAnswerTypeId) {
              case EnumService.CustomAnswerType.NumberFieldInteger:
                valueToBeCompared = parseInt(logic.integerValue, 0);
                selectedValue = parseInt(value, 0);
                break;
              case EnumService.CustomAnswerType.NumberFieldDecimal:
                valueToBeCompared = parseFloat(logic.decimalValue);
                selectedValue = parseFloat(value);
                break;
              case EnumService.CustomAnswerType.DateTimeField:
                const dtDateValue = logic.dateValue;
                const dtTimeValue = logic.timeValue;
                const dtHourFormatID = logic.hourFormatID;
                const dtDateSplit = dtDateValue.split("/");
                const dtTimeValueSplit = dtTimeValue.split(" ");
                valueToBeCompared =
                  dtDateSplit[2] +
                  "-" +
                  dtDateSplit[1] +
                  "-" +
                  dtDateSplit[0] +
                  " " +
                  dtTimeValueSplit[0] +
                  " " +
                  (dtHourFormatID === 1 ? "AM" : "PM");

                selectedValue = moment(value)
                  .utc()
                  .format(StaticDataService.dateTimeFormatAMPM);

                dateObjToBeCompared = moment(
                  valueToBeCompared,
                  StaticDataService.dateTimeFormatAMPM
                );
                selectedDateObj = moment(
                  selectedValue,
                  StaticDataService.dateTimeFormatAMPM
                );
                isDateTimeObject = true;
                break;
              case EnumService.CustomAnswerType.DateField:
                const dateValue = logic.dateValue;
                const dateSplit = dateValue.split("/");
                valueToBeCompared =
                  dateSplit[2] + "-" + dateSplit[1] + "-" + dateSplit[0];

                selectedValue = moment(value)
                  .utc()
                  .format(StaticDataService.dateFormat);

                dateObjToBeCompared = moment(
                  valueToBeCompared,
                  StaticDataService.dateFormat
                );
                selectedDateObj = moment(
                  selectedValue,
                  StaticDataService.dateFormat
                );
                isDateTimeObject = true;
                break;
              case EnumService.CustomAnswerType.TimeField:
                const timeValue = logic.timeValue;
                const hourFormatID = logic.hourFormatID;
                const timeValueSplit = timeValue.split(" ");
                valueToBeCompared =
                  timeValueSplit[0] + "" + (hourFormatID === 1 ? "AM" : "PM");

                selectedValue = moment(value)
                  .utc()
                  .format(StaticDataService.timeFormat);

                dateObjToBeCompared = moment(
                  valueToBeCompared,
                  StaticDataService.timeFormat
                );
                selectedDateObj = moment(
                  selectedValue,
                  StaticDataService.timeFormat
                );
                isDateTimeObject = true;
                break;
            }

            if (
              isDateTimeObject &&
              ((questionOperatorTypeID ===
                EnumService.QuestionLogic.OtherValueOperationType.EqualTo &&
                selectedDateObj.isSame(dateObjToBeCompared)) ||
                (questionOperatorTypeID ===
                  EnumService.QuestionLogic.OtherValueOperationType.LessThen &&
                  selectedDateObj.isBefore(dateObjToBeCompared)) ||
                (questionOperatorTypeID ===
                  EnumService.QuestionLogic.OtherValueOperationType
                    .GreaterThen &&
                  selectedDateObj.isAfter(dateObjToBeCompared)) ||
                (questionOperatorTypeID ===
                  EnumService.QuestionLogic.OtherValueOperationType
                    .LessThenEqualTo &&
                  selectedDateObj.isSameOrBefore(dateObjToBeCompared)) ||
                (questionOperatorTypeID ===
                  EnumService.QuestionLogic.OtherValueOperationType
                    .GreaterThenEqualTo &&
                  selectedDateObj.isSameOrAfter(dateObjToBeCompared)) ||
                (questionOperatorTypeID ===
                  EnumService.QuestionLogic.OtherValueOperationType
                    .NotEqualTo &&
                  !selectedDateObj.isSame(dateObjToBeCompared)))
            ) {
              isActionMeet = true;
            } else if (
              (questionOperatorTypeID ===
                EnumService.QuestionLogic.OtherValueOperationType.EqualTo &&
                selectedValue === valueToBeCompared) ||
              (questionOperatorTypeID ===
                EnumService.QuestionLogic.OtherValueOperationType.LessThen &&
                selectedValue < valueToBeCompared) ||
              (questionOperatorTypeID ===
                EnumService.QuestionLogic.OtherValueOperationType.GreaterThen &&
                selectedValue > valueToBeCompared) ||
              (questionOperatorTypeID ===
                EnumService.QuestionLogic.OtherValueOperationType
                  .LessThenEqualTo &&
                selectedValue <= valueToBeCompared) ||
              (questionOperatorTypeID ===
                EnumService.QuestionLogic.OtherValueOperationType
                  .GreaterThenEqualTo &&
                selectedValue >= valueToBeCompared) ||
              (questionOperatorTypeID ===
                EnumService.QuestionLogic.OtherValueOperationType.NotEqualTo &&
                selectedValue !== valueToBeCompared)
            ) {
              isActionMeet = true;
            }
          }
          if (isActionMeet) {
            this.applyLogicOn(
              question,
              logic,
              sections,
              sectionIndex,
              formGroup
            );
          }
        }
      });

      this.addFormControlsForVisibleFields(sections, formGroup);
    }
  }

  /**
   * Reset all applied logics for passed question
   * @param question
   * @param sections
   */
  resetAppliedLogicByQuestion = (
    question,
    sections,
    currentSectionIndex,
    formGroup
  ) => {
    const questionLogics = question.questionLogics;

    // Reset applied logic from this question
    questionLogics.some((logic) => {
      if (
        EnumService.QuestionLogic.LogicApplicableForQuestionTypes.indexOf(
          question.selectedAnswerTypeId
        ) !== -1
      ) {
        if (
          logic.questionActionTypeID ===
          EnumService.QuestionLogic.ActionType.MarkAsFailed
        ) {
          delete question[
            EnumService.QuestionLogic.ActionTypeForForm.MarkAsFailed
          ];
        } else if (
          logic.questionActionTypeID ===
          EnumService.QuestionLogic.ActionType.Notify
        ) {
          delete question[EnumService.QuestionLogic.ActionTypeForForm.Notify];
        } else if (
          logic.questionActionTypeID ===
          EnumService.QuestionLogic.ActionType.CreateNewActivity
        ) {
          delete question[
            EnumService.QuestionLogic.ActionTypeForForm.CreateNewActivity
          ];
        } else {
          const questionActionOnID = logic.questionActionOnID;
          const sectionAndQuestionNo = questionActionOnID.split("-");
          const sectionIndex = sectionAndQuestionNo[0] - 1;
          const questionIndex = sectionAndQuestionNo[1] - 1;

          let sectionObject;
          let questionObject;

          // For find current index to insert new duplicate item
          let currentIndexOfSection = 0;
          let currentIndexOfQuestion = 0;

          if (sectionIndex >= 0) {
            // Find Section/Question Index that was changed by current logic
            for (
              let sectionKey = 0;
              sectionKey < sections.length;
              sectionKey++
            ) {
              const sectionItem = sections[sectionKey];

              const isQuestionChanged = sectionIndex >= 0 && questionIndex >= 0;
              const isSectionChanged = sectionIndex >= 0 && questionIndex < 0;

              if (isQuestionChanged) {
                for (
                  let questionKey = 0;
                  questionKey < sectionItem.questions.length;
                  questionKey++
                ) {
                  const questionItem = sectionItem.questions[questionKey];
                  if (
                    questionItem["applied_relation_ids"] &&
                    logic["applied_relation_id"] &&
                    questionItem["applied_relation_ids"].indexOf(
                      logic["applied_relation_id"]
                    ) != -1
                  ) {
                    questionObject = questionItem;
                    currentIndexOfSection = sectionKey;
                    currentIndexOfQuestion = questionKey;
                    break;
                  }
                }
              } else if (isSectionChanged) {
                if (
                  sectionItem["applied_relation_ids"] &&
                  logic["applied_relation_id"] &&
                  sectionItem["applied_relation_ids"].indexOf(
                    logic["applied_relation_id"]
                  ) != -1
                ) {
                  sectionObject = sectionItem;
                  currentIndexOfSection = sectionKey;
                  break;
                }
              }
            }

            // let duplicateSectionCount = 0;

            // for (let sectionKey = 0; sectionKey < sections.length; sectionKey++) {
            // 	const sectionItem = sections[sectionKey];
            // 	if (sectionItem[EnumService.QuestionLogic.ActionTypeForForm.Duplicate]) {
            // 		duplicateSectionCount++;
            // 	}

            // 	if (sectionKey === sectionIndex + duplicateSectionCount) {
            // 		if (sectionIndex >= 0 && questionIndex >= 0) {
            // 			let duplicateQuestionCount = 0;

            // 			for (let questionKey = 0; questionKey < sectionItem.questions.length; questionKey++) {
            // 				const questionItem = sectionItem.questions[questionKey];
            // 				if (questionItem[EnumService.QuestionLogic.ActionTypeForForm.Duplicate]) {
            // 					duplicateQuestionCount++;
            // 				}
            // 				if (questionKey === questionIndex + duplicateQuestionCount) {
            // 					questionObject = questionItem;
            // 					currentIndexOfSection = sectionIndex + duplicateSectionCount;
            // 					currentIndexOfQuestion = questionIndex + duplicateQuestionCount;
            // 					break;
            // 				}
            // 			}
            // 		} else if (sectionIndex >= 0 && questionIndex < 0) {
            // 			sectionObject = sectionItem;
            // 			currentIndexOfSection = sectionIndex + duplicateSectionCount;
            // 		}
            // 		break;
            // 	}
            // }
          }

          if (
            logic.questionActionTypeID ===
            EnumService.QuestionLogic.ActionType.Show
          ) {
            if (sectionObject) {
              delete sectionObject[
                EnumService.QuestionLogic.ActionTypeForForm.ShowForLogic
              ];
              //logic.questionActionTypeID = 2
              sectionObject.sectionIsHidden = true
              // console.log('from two add hidden section', sectionObject)
              this.addHiddenSection(sectionObject)
              //sectionObject.sectionIsHidden = !sectionObject.sectionIsHidden
            } else if (questionObject) {
              delete questionObject[
                EnumService.QuestionLogic.ActionTypeForForm.ShowForLogic
              ];

              //Reset for sublogic applied
              const controlName = UtilService.FCUniqueName(
                sections[currentSectionIndex],
                questionObject
              );
              //formGroup.controls[controlName]?.reset();
              this.resetAppliedLogicByQuestion(
                questionObject,
                sections,
                currentSectionIndex,
                formGroup
              );
              //logic.questionActionTypeID = 2
              questionObject.questionIsHidden = true
              if(logic.questionActionOnID.split("-")[1].toString() === "0" ){
                //console.log("section showhide logic:", logic.questionActionOnID)
                // console.log('from three add hidden section')
                this.addHiddenSection(sectionObject)
              } else {
                //console.log("question showhide logic:", logic.questionActionOnID)
                  this.addHiddenQuestion(questionObject)
              }
              //questionObject.questionIsHidden = !questionObject.questionIsHidden
            }
          } else if (
            logic.questionActionTypeID ===
            EnumService.QuestionLogic.ActionType.Hide
          ) {
            if (sectionObject) {
              delete sectionObject[
                EnumService.QuestionLogic.ActionTypeForForm.HideForLogic
              ];
              //logic.questionActionTypeID = 1
              //sectionObject.sectionIsHidden = true
              // console.log('from one', sectionObject);
              sectionObject.sectionIsHidden = false;
              this.removeHiddenSection(sectionObject.sectionId)
            } else if (questionObject) {
              delete questionObject[
                EnumService.QuestionLogic.ActionTypeForForm.HideForLogic
              ];
              //logic.questionActionTypeID = 1
              //questionObject.questionIsHidden = true
              this.removeHiddenQuestion(questionObject.questionId)
            }
          } else if (
            logic.questionActionTypeID ===
            EnumService.QuestionLogic.ActionType.Duplicate
          ) {
            if (sectionObject) {
              sections.some((section, key) => {
                const uniquePreString =
                  question.questionId + "" + logic.questionLogicId;
                if (
                  section[
                  EnumService.QuestionLogic.ActionTypeForForm.Duplicate
                  ] &&
                  section[
                  EnumService.QuestionLogic
                    .FormControlNamePreStringForUniqueName
                  ] === uniquePreString
                ) {
                  sections.splice(key, 1);
                }
              });
            } else if (questionObject) {
              sections[currentIndexOfSection].questions.some(
                (questionInner, key) => {
                  const uniquePreString =
                    question.questionId + "" + logic.questionLogicId;
                  if (
                    questionInner[
                    EnumService.QuestionLogic.ActionTypeForForm.Duplicate
                    ] &&
                    questionInner[
                    EnumService.QuestionLogic
                      .FormControlNamePreStringForUniqueName
                    ] === uniquePreString
                  ) {
                    sections[currentIndexOfSection].questions.splice(key, 1);
                  }
                }
              );
            }
          }
        }
      }
    });
    // -- End
  };

  applyLogicOn(question, logic, sections, currentSectionIndex, formGroup) {
    //console.log("called apply logic, question: ", question, " logic: ", logic, "type: ", logic.questionActionTypeID)  //1-2 are show, hide 
    if (
      logic.questionActionTypeID ===
      EnumService.QuestionLogic.ActionType.MarkAsFailed
    ) {
      question[EnumService.QuestionLogic.ActionTypeForForm.MarkAsFailed] = true;
    } else if (
      logic.questionActionTypeID === EnumService.QuestionLogic.ActionType.Notify
    ) {
      question[EnumService.QuestionLogic.ActionTypeForForm.Notify] = true;
    } else if (
      logic.questionActionTypeID ===
      EnumService.QuestionLogic.ActionType.CreateNewActivity
    ) {
      question[EnumService.QuestionLogic.ActionTypeForForm.CreateNewActivity] =
        true;
    } else {
      const questionActionTypeID = logic.questionActionTypeID;
      const questionActionOnID = logic.questionActionOnID;
      const sectionAndQuestionNo = questionActionOnID.split("-");
      const sectionIndex = sectionAndQuestionNo[0] - 1;
      const questionIndex = sectionAndQuestionNo[1] - 1;

      let sectionObject;
      let questionObject;

      // For find current index to insert new duplicate item
      let currentIndexOfSection = 0;
      let currentIndexOfQuestion = 0;

      // TO find original question/section index except duplicate question/section index
      if (sectionIndex >= 0) {
        let duplicateSectionCount = 0;

        const currentSection = sections[currentSectionIndex];

        let isValueFound = false;

        for (let sectionKey = 0; sectionKey < sections.length; sectionKey++) {
          const sectionItem = sections[sectionKey];
          if (
            sectionItem[EnumService.QuestionLogic.ActionTypeForForm.Duplicate]
          ) {
            duplicateSectionCount++;
          }

          if (sectionKey === sectionIndex + duplicateSectionCount) {
            if (sectionIndex >= 0 && questionIndex >= 0) {
              let duplicateQuestionCount = 0;

              let questions;
              if (sectionItem.sectionId === currentSection.sectionId) {
                questions = currentSection.questions;
              } else {
                questions = sectionItem.questions;
              }
              for (
                let questionKey = 0;
                questionKey < questions.length;
                questionKey++
              ) {
                const questionItem = questions[questionKey];
                if (
                  questionItem[
                  EnumService.QuestionLogic.ActionTypeForForm.Duplicate
                  ]
                ) {
                  duplicateQuestionCount++;
                }
                if (questionKey === questionIndex + duplicateQuestionCount) {
                  questionObject = questionItem;
                  currentIndexOfSection = sectionKey;
                  currentIndexOfQuestion = questionKey;
                  isValueFound = true;
                  break;
                }
              }
            } else if (sectionIndex >= 0 && questionIndex < 0) {
              sectionObject = sectionItem;
              currentIndexOfSection = sectionKey;
              isValueFound = true;
              break;
            }
          }
          if (isValueFound) {
            break;
          }
        }
      }

      if (questionActionTypeID === EnumService.QuestionLogic.ActionType.Show) {
        if (sectionObject) {
          if(!sectionObject.isAccidentReportSection && !sectionObject.isHAVSection && !sectionObject.isRiskAssessmentSection){
            sectionObject[
              EnumService.QuestionLogic.ActionTypeForForm.ShowForLogic
            ] = true;
            this.setUniqueRelationIdOnLogicAndQuestionOrSection(
              sectionObject,
              logic
            );
            //sectionObject.questionActionTypeID = 2
            sectionObject.sectionIsHidden = false
            //sectionObject.sectionIsHidden = !sectionObject.sectionIsHidden
            // console.log('from two', sectionObject);
            this.removeHiddenSection(sectionObject.sectionId)
          } else{
            console.log("skipped visibility logic for predefined/required section: ", sectionObject.sectionId)
          }
        } else if (questionObject) {
          questionObject[
            EnumService.QuestionLogic.ActionTypeForForm.ShowForLogic
          ] = true;
          this.setUniqueRelationIdOnLogicAndQuestionOrSection(
            questionObject,
            logic
          );
          //question.questionActionTypeID = 2
          questionObject.questionIsHidden = false
          //questionObject.questionIsHidden = !questionObject.questionIsHidden
          this.removeHiddenQuestion(questionObject.questionId)
        }
      } else if (
        questionActionTypeID === EnumService.QuestionLogic.ActionType.Hide
      ) {
        if (sectionObject) {
          if(!sectionObject.isAccidentReportSection && !sectionObject.isHAVSection && !sectionObject.isRiskAssessmentSection){
            sectionObject[
              EnumService.QuestionLogic.ActionTypeForForm.HideForLogic
            ] = true;
            this.setUniqueRelationIdOnLogicAndQuestionOrSection(
              sectionObject,
              logic
            );
            //sectionObject.questionActionTypeID = 1
            sectionObject.sectionIsHidden = true
            // console.log('from one add hidden section', sectionObject);
            this.addHiddenSection(sectionObject)
          } else{
            console.log("skipped visibility logic for predefined/required section: ", sectionObject.sectionId)
          }
        } else if (questionObject) {
          this.hideLogicExecutedSource.next({
            type: 'question', 
            id: questionObject.questionId
          });
          questionObject[
            EnumService.QuestionLogic.ActionTypeForForm.HideForLogic
          ] = true;
          this.setUniqueRelationIdOnLogicAndQuestionOrSection(
            questionObject,
            logic
          );
          //question.questionActionTypeID = 1
          //questionObject.questionIsHidden = true
          this.addHiddenQuestion(questionObject)
        }
      } else if (
        questionActionTypeID === EnumService.QuestionLogic.ActionType.Duplicate
      ) {
        if (sectionObject) {
          const duplicateSection = JSON.parse(JSON.stringify(sectionObject));

          if (duplicateSection.sectionIsHidden) {
            duplicateSection.sectionIsHidden = false;
          }
          duplicateSection[
            EnumService.QuestionLogic.ActionTypeForForm.Duplicate
          ] = true;
          duplicateSection[
            EnumService.QuestionLogic.FormControlNamePreStringForUniqueName
          ] = question.questionId + "" + logic.questionLogicId;

          try {
            this.removeLogicHelpersFromObjectForDuplicate(duplicateSection);
            duplicateSection.questions?.map((quesItem) => {
              const controlName = UtilService.FCUniqueName(
                duplicateSection,
                quesItem
              );
              formGroup?.controls[controlName]?.reset();
              this.removeLogicHelpersFromObjectForDuplicate(quesItem);
            });
          } catch (error) { }

          sections.splice(currentIndexOfSection + 1, 0, duplicateSection);
          this.setUniqueRelationIdOnLogicAndQuestionOrSection(
            duplicateSection,
            logic
          );
        } else if (questionObject) {
          const duplicateQuestion = JSON.parse(JSON.stringify(questionObject));
          duplicateQuestion[
            EnumService.QuestionLogic.ActionTypeForForm.Duplicate
          ] = true;
          if (duplicateQuestion.questionIsHidden) {
            duplicateQuestion.questionIsHidden = false;
          }
          duplicateQuestion[
            EnumService.QuestionLogic.FormControlNamePreStringForUniqueName
          ] = question.questionId + "" + logic.questionLogicId;

          try {
            const controlName = UtilService.FCUniqueName(
              sections[currentSectionIndex],
              duplicateQuestion
            );
            formGroup?.controls[controlName]?.reset();
            this.removeLogicHelpersFromObjectForDuplicate(duplicateQuestion);
          } catch (error) { }

          sections[currentIndexOfSection].questions.splice(
            currentIndexOfQuestion + 1,
            0,
            duplicateQuestion
          );
          this.setUniqueRelationIdOnLogicAndQuestionOrSection(
            duplicateQuestion,
            logic
          );

          duplicateQuestion.answerChoiceAttributes = duplicateQuestion.answerChoiceAttributes.map(answer => {
          const uniquePrefix = this.Uniqueid() + '_';
          return {
            ...answer,
            uiId: uniquePrefix + answer.answerChoiceAttributeId,
          };
        });

          duplicateQuestion.value = null;
          duplicateQuestion.selectedValue = null;
        }
      } else if (
        questionActionTypeID ===
        EnumService.QuestionLogic.ActionType.MarkAsFailed
      ) {
        if (sectionObject) {
          sectionObject[
            EnumService.QuestionLogic.ActionTypeForForm.MarkAsFailed
          ] = true;
          this.setUniqueRelationIdOnLogicAndQuestionOrSection(
            sectionObject,
            logic
          );
        } else if (questionObject) {
          questionObject[
            EnumService.QuestionLogic.ActionTypeForForm.MarkAsFailed
          ] = true;
          this.setUniqueRelationIdOnLogicAndQuestionOrSection(
            questionObject,
            logic
          );
        }
      } else if (
        questionActionTypeID === EnumService.QuestionLogic.ActionType.Notify
      ) {
        if (sectionObject) {
          sectionObject[EnumService.QuestionLogic.ActionTypeForForm.Notify] =
            true;
          this.setUniqueRelationIdOnLogicAndQuestionOrSection(
            sectionObject,
            logic
          );
        } else if (questionObject) {
          questionObject[EnumService.QuestionLogic.ActionTypeForForm.Notify] =
            true;
          this.setUniqueRelationIdOnLogicAndQuestionOrSection(
            questionObject,
            logic
          );
        }
      } else if (
        questionActionTypeID ===
        EnumService.QuestionLogic.ActionType.CreateNewActivity
      ) {
        if (sectionObject) {
          sectionObject[
            EnumService.QuestionLogic.ActionTypeForForm.CreateNewActivity
          ] = true;
          this.setUniqueRelationIdOnLogicAndQuestionOrSection(
            sectionObject,
            logic
          );
        } else if (questionObject) {
          questionObject[
            EnumService.QuestionLogic.ActionTypeForForm.CreateNewActivity
          ] = true;
          this.setUniqueRelationIdOnLogicAndQuestionOrSection(
            questionObject,
            logic
          );
        }
      }
    }
  }

  removeLogicHelpersFromObjectForDuplicate(newObject) {
    delete newObject[EnumService.QuestionLogic.ActionTypeForForm.HideForLogic];
    delete newObject[EnumService.QuestionLogic.ActionTypeForForm.ShowForLogic];
    delete newObject[EnumService.QuestionLogic.ActionTypeForForm.MarkAsFailed];
    delete newObject[EnumService.QuestionLogic.ActionTypeForForm.Notify];
    delete newObject[
      EnumService.QuestionLogic.ActionTypeForForm.CreateNewActivity
    ];
  }

  setUniqueRelationIdOnLogicAndQuestionOrSection(question, logic) {
    const uniqueId = this.Uniqueid();
    logic["applied_relation_id"] = uniqueId;
    let relationIds = question["applied_relation_ids"];
    if (!relationIds) {
      relationIds = [];
    }
    relationIds.push(uniqueId);
    question["applied_relation_ids"] = relationIds;
  }

  removeFieldIfAdded(formGroup: UntypedFormGroup, formControlName) {
    if (formGroup?.controls[formControlName]) {
      formGroup?.removeControl(formControlName);
    }
  }

  // Extract id from string (eg. L|10)
  getRelevantEntityId = (entityId) => {
    const response = {
      InventoryID: null,
      ProjectID: null,
      LocationID: null,
      DocumentID: null,
      FormID: null,
    };
    const entityIdSplits = entityId.split("|");
    if (entityIdSplits && entityIdSplits.length >= 2) {
      if (entityIdSplits[0] === "I") {
        response.InventoryID = parseInt(entityIdSplits[1], 0);
      } else if (entityIdSplits[0] === "P") {
        response.ProjectID = parseInt(entityIdSplits[1], 0);
      } else if (entityIdSplits[0] === "L") {
        response.LocationID = parseInt(entityIdSplits[1], 0);
      } else if (entityIdSplits[0] === "F") {
        response.FormID = parseInt(entityIdSplits[1], 0);
      } else if (entityIdSplits[0] === "D") {
        response.DocumentID = parseInt(entityIdSplits[1], 0);
      } else {
        response.LocationID = parseInt(entityIdSplits[1], 0);
      }
    }
    return response;
  };

  getEntityIdFromId(locationDetails) {
    let entityId = "";
    if (locationDetails?.locationID > 0) {
      entityId = "L|" + locationDetails?.locationID;
    } else if (locationDetails?.projectID > 0) {
      entityId = "P|" + locationDetails?.projectID;
    } else if (locationDetails?.inventoryItemID > 0) {
      entityId = "I|" + locationDetails?.inventoryItemID;
    }
    return entityId;
  }
  /**
   * Replaces the displayed url with the input string.
   * 
   * FOR AESTHETIC USE ONLY
   * 
   * DO NOT USE TO DELETE DATA OR BEFORE ANY FUNCTIONAL USES OF QUERY PARAM DATA
   * @param url - the new aesthetic url
   */
  updateUrlWithoutQueryParams(url:string) {
    const newUrl = `${window.location.origin}/${url}`;
    history.replaceState(null,'', newUrl);
  }
  addHiddenSection(sectionObject: any) {
    let isDupe = false
    for(const section of this.hiddenSections){
      if(section.sectionId === sectionObject.sectionId){
        isDupe =  true
      }
    }if (!isDupe){
      const updatedSections = [...this.hiddenSections, sectionObject];
      this.hiddenSectionsSubject.next(updatedSections);
    }
  }

  removeHiddenSection(sectionId: string) {
    // console.log('trigger remove hidden section');
    // const updatedSections = this.hiddenSections.filter(
    //   (item) => item !== sectionId
    // );
    const updatedSections = JSON.parse(JSON.stringify(this.hiddenSections)).filter(
      (item) => item.sectionId !== sectionId
    );
    this.hiddenSectionsSubject.next(updatedSections);
    this.removeHiddenSectionTriggered$.next(sectionId);
  }
  addHiddenQuestion(questionObject: any) {
    // console.log('now execute', questionObject);
    const updatedQuestions = this.hiddenQuestionsSubject.value.filter(q => q.questionId !== questionObject.questionId);
    updatedQuestions.push(questionObject);
    this.hiddenQuestionsSubject.next(updatedQuestions);
    this.addHiddenQuestionTriggered$.next({
      questionId: questionObject.questionId,
      sectionId: questionObject.sectionID
    })
  }


  removeHiddenQuestion(questionId: string) {
    const updatedQuestions = this.hiddenQuestions.filter(
      (item) => item.questionId !== questionId
    );
    //for(const item of this.hiddenQuestions){} //had something to do with updatedQuestions
    this.hiddenQuestionsSubject.next(updatedQuestions);
  }
  initaliseHiddenScoringElements(sections: any[]){
    for(const section of sections){
      if (section.sectionIsHidden){
        //try{this.removeHiddenSection(section.sectionId)}catch{}
        this.addHiddenSection(section)
        for(const question of section.questions){
          this.addHiddenQuestion(question) //TODO: had.questionID before, put back if causes issues
        }
      } 
      else{
        for(const question of section.questions){
          if(question.questionIsHidden){
            this.addHiddenQuestion(question) //TODO: had.questionID before, put back if causes issues
          }
        }
      }
    }
    // console.log("initalised list: ", JSON.parse(JSON.stringify(this.hiddenSections)), JSON.parse(JSON.stringify(this.hiddenQuestions)))
    //return([this.hiddenSections, JSON.parse(JSON.stringify(this.hiddenQuestions))])
    return([JSON.parse(JSON.stringify(this.hiddenSections)), JSON.parse(JSON.stringify(this.hiddenQuestions))])
  }

  // private logHideLogicExecution(question: any, logic: any, target: any) {
  //   if (logic.questionActionTypeID === EnumService.QuestionLogic.ActionType.Hide) {
  //     if (target?.questionId) {
  //       console.log(`Hide logic executed on QUESTION ID: ${target.questionId}`);
  //     } else if (target?.sectionId) {
  //       console.log(`Hide logic executed on SECTION ID: ${target.sectionId}`);
  //     }
  //   }
  // }

  wipeHiddenSectionsAndQuestions(){
    this.hiddenSectionsSubject.next([]);
    this.hiddenQuestionsSubject.next([]);
  }

  targetedLogics = []
  getTargetedLogics(formBuilderDetail){
    const initLogics =[]
    for(const section of formBuilderDetail.sections){
      for(const question of section.questions){
        for(const logic of question.questionLogics){
          let targetSection = logic.questionActionOnID.split("-")[0]
          let targetQuestion = logic.questionActionOnID.split("-")[1]
          if(targetSection !="0" && targetQuestion !==null && targetQuestion !== undefined){
            initLogics.push({
              id:logic.questionLogicId,
              source: logic.questionID,
              questionLogicNumber: logic.questionChoiceSetSequence,
              logicAnswerChoice: question.answerChoiceAttributes[logic.questionChoiceSetSequence -1].answerChoiceAttributeId,
              type: logic.questionActionTypeID,
              targetSection: targetSection,
              targetQuestion: targetQuestion
            })
          }
        }
      }
    };
    for(const targetedLogic of  initLogics){
      for(const section of formBuilderDetail.sections){
        if(section.sectionDisplayOrder == targetedLogic.targetSection){
          if(targetedLogic.targetQuestion != "0"){
            for(const question of section.questions){
              if(question.questionDisplayOrder == targetedLogic.targetQuestion){
                if(!this.targetedLogics.some(item => item.id === targetedLogic.id)) {
                  this.targetedLogics.push({
                    id:targetedLogic.id,
                    source: targetedLogic.source,
                    questionLogicNumber: targetedLogic.questionLogicNumber,
                    logicAnswerChoice: targetedLogic.logicAnswerChoice,
                    type: targetedLogic.type,
                    target: question.questionId
                  })
                }
                break
              }
            }
          } else{
            if(!this.targetedLogics.some(item => item.id === targetedLogic.id)) {
              this.targetedLogics.push({
                id:targetedLogic.id,
                source: targetedLogic.source,
                questionLogicNumber: targetedLogic.questionLogicNumber,
                logicAnswerChoice: targetedLogic.logicAnswerChoice,
                type: targetedLogic.type,
                target: section.sectionId
              })
            }
          }
        }
      }      
    }
    console.log("updated targeted logics: ", this.targetedLogics)
  }
}
